import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../lib/utils';
import { chiusureLocaliQuery, plurale, type ChiusuraLocaleRow } from './adminApi';

/**
 * Chiuse qui — le rate che risultano saldate dentro Masamune OS mentre su
 * FattureInCloud sono ancora aperte.
 *
 * A cosa serve: da quando un pagamento si chiude solo qui (e su FiC non si
 * scrive più), FiC e il gestionale divergono per costruzione. Questa è la lista
 * di quella divergenza, cioè di cosa un giorno andrà riportato al
 * commercialista, con il perché scritto accanto.
 *
 * Non è una lista che cresce all'infinito: quando FiC recupera il pagamento, il
 * sync azzera la marcatura e la riga sparisce da sola. Perciò il contatore in
 * cima si legge come "quanto lavoro FiC non sa ancora".
 *
 * Stato in URL: q
 */
const PAGE_SIZE = 50;

export function ChiuseQuiTab() {
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search]);

  const params = useMemo(() => ({ search: search.trim() || undefined, page, pageSize: PAGE_SIZE }), [search, page]);
  const q = useQuery(chiusureLocaliQuery(params));
  const items = q.data?.items ?? [];
  const totals = q.data?.totals;

  return (
    <>
      <MobileFilters attivi={search.trim() ? 1 : 0} label="Cerca">
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <div className="tb-search flex-1" style={{ minWidth: 200, margin: 0 }}>
          <Icon name="search" size={12} />
          <input aria-label="Cerca fra le chiusure locali" placeholder="Cerca cliente, fornitore, numero o motivo…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <span className="mute text-[12px]">{plurale(q.data?.total ?? 0, 'rata', 'rate')}</span>
        {totals && (
          <>
            <div className="divider" />
            <span className="mute text-[12px]">
              incassi <strong className="mono">€ {fmtEur(totals.incassi, true)}</strong> · uscite <strong className="mono">€ {fmtEur(totals.uscite, true)}</strong>
            </span>
          </>
        )}
      </div>
      </MobileFilters>

      {/* `mb-3` (12px) dove `.adm-note` porta già i suoi 18: era l'unico dei
          quattro avvisi dell'Amministrazione a staccarsi diversamente. */}
      <div className="adm-note">
        <Icon name="help" size={12} className="mute" />
        <span>
          Saldate qui dentro, su FattureInCloud ancora aperte. Nessuna di queste è stata scritta su FiC: quando il
          commercialista le registra, spariscono da sole al primo aggiornamento.
        </span>
      </div>

      {q.isLoading && !q.data ? (
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={5} cols={6} withAvatar={false} /></tbody></table></div>
      ) : items.length === 0 ? (
        search.trim()
          ? <EmptyState brand title="Nessun risultato" description="Azzera la ricerca per vedere tutte le chiusure." />
          : <EmptyState brand title="Niente in sospeso" description="Ogni pagamento segnato qui risulta anche su FattureInCloud." />
      ) : (
        <div className="ed-section">
          {/* `tbl--static`: sul telefono ogni riga è una scheda, ma qui non si
              apre niente (è una lista da leggere), quindi niente chevron. */}
          <table className="tbl tbl--ed tbl--card tbl--static">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Tipo</th>
                <th>Documento</th>
                <th style={{ width: 150 }}>Chiusa il</th>
                <th className="num" style={{ width: 130 }}>Importo</th>
                <th>Perché</th>
                <th style={{ width: 190 }}>Movimento</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => <Row key={`${r.kind}-${r.id}`} r={r} />)}
            </tbody>
          </table>
          {q.data && <Pagination page={page} pageSize={q.data.pageSize} total={q.data.total} totalPages={q.data.totalPages} onPage={setPage} />}
        </div>
      )}
    </>
  );
}

function Row({ r }: { r: ChiusuraLocaleRow }) {
  return (
    <tr style={{ cursor: 'default' }}>
      <td data-m="key" data-l="Tipo">
        {r.kind === 'INCASSO' ? <Chip tone="ok">Incasso</Chip> : <Chip tone="warn">Uscita</Chip>}
      </td>
      <td data-m="hd" className="adm-clip">
        <div className="font-semibold truncate">{r.counterparty}</div>
        <div className="text-[12px] mute">
          {r.docNumber ?? 's/n'}{r.seq > 1 ? ` · rata ${r.seq}` : ''}
          {/* Senza documento su FiC non c'è niente da riportare: è già tutto e solo nostro. */}
          {!r.onFic && ' · non su FiC'}
        </div>
      </td>
      <td data-m="key" data-l="Chiusa il" className="mono text-[13px]" style={{ whiteSpace: 'nowrap' }}>
        {fmtDate(r.localPaidAt)}
        {r.paidDate && r.paidDate !== r.localPaidAt && <div className="text-[12px] mute">valuta {fmtDate(r.paidDate)}</div>}
      </td>
      <td data-m="key" data-l="Importo" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(r.amount, true)}</td>
      {/* Il motivo va in un <div>, non in uno <span>: `.adm-clip` tronca solo i
          figli di blocco (è lo stesso appunto scritto in RiconciliazioneTab).
          Con lo <span> il testo lungo della riconciliazione automatica non si
          tagliava, la tabella cresceva fino a 1448px dentro 1064 e il "Perché"
          finiva stampato sopra la colonna "Movimento". */}
      <td data-m="row" data-l="Perché" className="adm-clip">
        {r.localPaidNote
          ? <div className="text-[13px]" title={r.localPaidNote}>{r.localPaidNote}</div>
          : <div className="mute text-[13px]">—</div>}
      </td>
      <td data-m="key" data-l="Movimento" className="text-[12px]" style={{ whiteSpace: 'nowrap' }}>
        {r.transaction
          ? <span className="mute" title={r.transaction.counterparty ?? ''}>{fmtDate(r.transaction.settledOn)} · € {fmtEur(r.transaction.amount, true)}</span>
          : <span className="mute">nessuno</span>}
      </td>
    </tr>
  );
}
